import { useEffect, useRef, useState } from 'react';
import { DEFAULT_TAPE_DECK_TRANSFORM } from './tapeDeckTransform';
import { tapeDeckThumbPublicUrl, tapeDeckVideoPublicUrl } from './voiceNoteAssets';

/** Margin from clip start/end when looping (seconds). */
const LOOP_MARGIN = 0.06;

/** REC / PLAY tape motor: media seconds per real second. */
const REVERSE_SPEED = 0.82;

const REVERSE_MANUAL_INTERVAL_MS = 50;

/** Seek out of `ended` / tail so a later `play()` is reliable (esp. 2nd+ cycles). */
function clampTimeForForwardLoop(v) {
  const d = v.duration;
  if (!d || !Number.isFinite(d)) return;
  if (v.ended || v.currentTime >= d - LOOP_MARGIN) {
    v.currentTime = LOOP_MARGIN;
    return;
  }
  if (v.currentTime < LOOP_MARGIN) {
    v.currentTime = LOOP_MARGIN;
  }
}

/** After idle at end of clip, reverse playback may not start until we're in-range. */
function seekForReverseStart(v) {
  const d = v.duration;
  if (!d || !Number.isFinite(d)) return;
  if (v.ended || v.currentTime >= d - LOOP_MARGIN * 0.5) {
    v.currentTime = Math.max(LOOP_MARGIN, d - LOOP_MARGIN);
  }
}

function useDeckPlayback(videoRef, idle, wantForward, wantReverse) {
  const playbackGenRef = useRef(0);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return undefined;

    const myGen = ++playbackGenRef.current;
    const alive = () => playbackGenRef.current === myGen;

    let forwardLoopHandler = /** @type {null | (() => void)} */ (null);
    let forwardEndedHandler = /** @type {null | (() => void)} */ (null);
    let reverseNativeLoopHandler = /** @type {null | (() => void)} */ (null);
    let reverseManualId = 0;

    const detachForwardLoop = () => {
      if (forwardLoopHandler) {
        v.removeEventListener('timeupdate', forwardLoopHandler);
        forwardLoopHandler = null;
      }
      if (forwardEndedHandler) {
        v.removeEventListener('ended', forwardEndedHandler);
        forwardEndedHandler = null;
      }
    };

    const detachReverseNativeLoop = () => {
      if (reverseNativeLoopHandler) {
        v.removeEventListener('timeupdate', reverseNativeLoopHandler);
        reverseNativeLoopHandler = null;
      }
    };

    const stopReverseManual = () => {
      if (reverseManualId !== 0) {
        window.clearInterval(reverseManualId);
        reverseManualId = 0;
      }
    };

    const hardStop = () => {
      detachForwardLoop();
      detachReverseNativeLoop();
      stopReverseManual();
      v.pause();
      v.playbackRate = 1;
    };

    const startForward = () => {
      try {
        v.loop = false;
      } catch {
        /* ignore */
      }
      v.playbackRate = 1;
      detachReverseNativeLoop();
      stopReverseManual();
      clampTimeForForwardLoop(v);

      const bumpForwardLoop = () => {
        if (!alive()) return;
        const d = v.duration;
        if (!d || !Number.isFinite(d)) return;
        v.currentTime = LOOP_MARGIN;
        void v.play().catch(() => {});
      };

      forwardLoopHandler = () => {
        if (!alive()) return;
        const d = v.duration;
        if (!d || !Number.isFinite(d)) return;
        if (v.currentTime < LOOP_MARGIN) {
          v.currentTime = LOOP_MARGIN;
          return;
        }
        /* Near EOF or paused after `ended`: seek and resume or loop stalls. */
        if (v.ended || v.currentTime >= d - LOOP_MARGIN) {
          bumpForwardLoop();
          return;
        }
        if (v.currentTime < d - LOOP_MARGIN * 4) return;
      };
      forwardEndedHandler = () => {
        bumpForwardLoop();
      };
      v.addEventListener('timeupdate', forwardLoopHandler);
      v.addEventListener('ended', forwardEndedHandler);
      void v.play().catch(() => {
        if (!alive()) return;
        clampTimeForForwardLoop(v);
        void v.play().catch(() => {});
      });
    };

    const startReverseManual = () => {
      detachReverseNativeLoop();
      v.pause();
      v.playbackRate = 1;
      const stepSec = (REVERSE_MANUAL_INTERVAL_MS / 1000) * REVERSE_SPEED;

      reverseManualId = window.setInterval(() => {
        if (!alive()) return;
        const d = v.duration;
        if (!d || !Number.isFinite(d) || d <= LOOP_MARGIN * 2) return;
        let t = v.currentTime - stepSec;
        if (t <= LOOP_MARGIN) t = d - LOOP_MARGIN;
        v.currentTime = t;
      }, REVERSE_MANUAL_INTERVAL_MS);
    };

    const tryNativeReverse = async () => {
      try {
        v.loop = false;
      } catch {
        /* ignore */
      }
      stopReverseManual();
      detachReverseNativeLoop();
      v.playbackRate = 1;
      seekForReverseStart(v);

      try {
        v.playbackRate = -REVERSE_SPEED;
        await v.play();
      } catch {
        if (alive()) startReverseManual();
        return;
      }

      if (!alive()) return;

      await new Promise((r) => requestAnimationFrame(r));
      if (!alive()) return;

      if (v.playbackRate >= 0) {
        v.pause();
        v.playbackRate = 1;
        startReverseManual();
        return;
      }

      const t0 = v.currentTime;
      await new Promise((r) => setTimeout(r, 120));
      if (!alive()) return;

      if (v.currentTime >= t0 - 0.001) {
        v.pause();
        v.playbackRate = 1;
        startReverseManual();
        return;
      }

      reverseNativeLoopHandler = () => {
        if (!alive() || !v.duration) return;
        if (v.currentTime <= LOOP_MARGIN) {
          v.currentTime = v.duration - LOOP_MARGIN;
          /* Some browsers pause on seek during reverse play. */
          if (v.paused) {
            void v.play().catch(() => {});
          }
        }
      };
      v.addEventListener('timeupdate', reverseNativeLoopHandler);
    };

    if (idle) {
      hardStop();
      return () => {
        playbackGenRef.current += 1;
        hardStop();
      };
    }

    /* Reverse before forward so a brief overlap (both true) still rewinds the reels. */
    if (wantReverse) {
      detachForwardLoop();
      hardStop();
      void tryNativeReverse();
      return () => {
        playbackGenRef.current += 1;
        hardStop();
      };
    }

    if (wantForward) {
      hardStop();
      startForward();
      return () => {
        playbackGenRef.current += 1;
        hardStop();
      };
    }

    hardStop();
    return () => {
      playbackGenRef.current += 1;
      hardStop();
    };
  }, [idle, wantForward, wantReverse, videoRef]);
}

/**
 * iOS/Safari often shows no decoded frame while `paused` until playback has started once.
 * When idle, seek and run a muted one-frame play so the reels are visible before REC/PLAY.
 */
function useIdleDeckVideoFirstFrame(videoRef, idle) {
  useEffect(() => {
    const v = videoRef.current;
    if (!v || !idle) return undefined;

    let cancelled = false;

    const paintStill = () => {
      if (cancelled || !videoRef.current) return;
      const el = videoRef.current;
      el.muted = true;
      el.playbackRate = 1;
      if (!Number.isFinite(el.duration) || el.duration <= LOOP_MARGIN * 2) return;

      const settle = () => {
        if (cancelled || !videoRef.current) return;
        videoRef.current.pause();
        videoRef.current.playbackRate = 1;
        clampTimeForForwardLoop(videoRef.current);
      };

      el.pause();
      clampTimeForForwardLoop(el);

      void el
        .play()
        .then(() => {
          if (cancelled) return;
          requestAnimationFrame(settle);
        })
        .catch(settle);
    };

    if (v.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
      paintStill();
    } else {
      v.addEventListener('loadeddata', paintStill, { once: true });
    }

    return () => {
      cancelled = true;
      if (v) {
        v.pause();
        v.playbackRate = 1;
      }
    };
  }, [idle, videoRef]);
}

/**
 * Mobile Safari often shows no decoded pixels until `play()`; keep a JPG cover until `loadeddata` / `playing`.
 */
function useTapeDeckThumbCover(videoRef) {
  const [coverOn, setCoverOn] = useState(true);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return undefined;

    const reveal = () => setCoverOn(false);

    if (v.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
      reveal();
      return undefined;
    }

    v.addEventListener('loadeddata', reveal, { once: true });
    v.addEventListener('canplay', reveal, { once: true });
    v.addEventListener('playing', reveal, { once: true });
    return () => {
      v.removeEventListener('loadeddata', reveal);
      v.removeEventListener('canplay', reveal);
      v.removeEventListener('playing', reveal);
    };
  }, [videoRef]);

  return coverOn;
}

/**
 * Looping deck video under base PNG (muted). Tape motor:
 * - `tapeReverse`: reel runs backward (listen rewind phase only)
 * - `tapeForward`: reel runs forward (REC capstan or listening playback)
 * - idle when neither (incl. paused listen)
 */
export function TapeDeckVideo({ tapeReverse, tapeForward }) {
  const videoRef = useRef(/** @type {HTMLVideoElement | null} */ (null));
  const [posterBroken, setPosterBroken] = useState(false);
  const thumbCoverOn = useTapeDeckThumbCover(videoRef);

  const idle = !tapeReverse && !tapeForward;

  useDeckPlayback(videoRef, idle, tapeForward, tapeReverse);
  useIdleDeckVideoFirstFrame(videoRef, idle);

  const deck = DEFAULT_TAPE_DECK_TRANSFORM;
  const s = deck.scale / 100;
  const thumbSrc = tapeDeckThumbPublicUrl();
  const posterUrl = posterBroken ? undefined : thumbSrc;

  return (
    <div
      className="tape-deck-video-wrap"
      style={{
        width: `${deck.widthPct}%`,
        transform: `translate(calc(-50% + ${deck.x}%), calc(-50% + ${deck.y}%)) scale(${s})`,
      }}
    >
      <video
        ref={videoRef}
        className="tape-deck-video"
        src={tapeDeckVideoPublicUrl()}
        poster={posterUrl}
        muted
        playsInline
        preload="auto"
        aria-hidden
      />
      {posterUrl ? (
        <img
          className={
            thumbCoverOn
              ? 'tape-deck-video-poster'
              : 'tape-deck-video-poster tape-deck-video-poster--hidden'
          }
          src={thumbSrc}
          alt=""
          draggable={false}
          aria-hidden
          onError={() => setPosterBroken(true)}
        />
      ) : null}
    </div>
  );
}
